<template>
  <v-container fluid>
    <v-card class="elevation-2 rounded-lg overflow-hidden">
      <!-- En-tête avec dégradé -->
      <div class="client-logs-header px-4 py-3">
        <div class="d-flex align-center">
          <v-icon color="white" class="mr-2">mdi-account-group</v-icon>
          <h2 class="text-h5 font-weight-bold white--text mb-0">Logs Clients</h2>
          <v-spacer></v-spacer>
          <v-btn
              color="white"
              small
              text
              @click="refreshData"
              :loading="refreshing"
              :disabled="loading"
              class="mr-2"
          >
            <v-icon left size="18">mdi-refresh</v-icon>
            Actualiser
          </v-btn>
        </div>
      </div>

      <!-- Indicateur de chargement -->
      <v-progress-linear
          :active="loading"
          :indeterminate="loading"
          color="primary"
          height="3"
      ></v-progress-linear>

      <!-- Statistiques des logs clients -->
      <v-card-text class="py-3 px-4">
        <v-row class="mb-3">
          <v-col cols="12" sm="6" md="4">
            <v-card elevation="1" class="pa-3 rounded-lg">
              <div class="d-flex align-center">
                <v-avatar color="primary lighten-4" size="42" class="mr-3">
                  <v-icon color="primary">mdi-account-multiple</v-icon>
                </v-avatar>
                <div>
                  <div class="text-caption grey--text">Total des logs</div>
                  <div class="text-h6 font-weight-bold">{{ clientslogs.length }}</div>
                </div>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-card elevation="1" class="pa-3 rounded-lg">
              <div class="d-flex align-center">
                <v-avatar color="info lighten-4" size="42" class="mr-3">
                  <v-icon color="info">mdi-calendar-today</v-icon>
                </v-avatar>
                <div>
                  <div class="text-caption grey--text">Aujourd'hui</div>
                  <div class="text-h6 font-weight-bold">{{ todayLogsCount }}</div>
                </div>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-card elevation="1" class="pa-3 rounded-lg">
              <div class="d-flex align-center">
                <v-avatar color="success lighten-4" size="42" class="mr-3">
                  <v-icon color="success">mdi-fingerprint</v-icon>
                </v-avatar>
                <div>
                  <div class="text-caption grey--text">Empreintes uniques</div>
                  <div class="text-h6 font-weight-bold">{{ uniqueFingerprintCount }}</div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Filtres -->
        <v-row>
          <v-col cols="12" sm="4">
            <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                label="Rechercher..."
                clearable
                hide-details
                outlined
                dense
                class="rounded-lg"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="4">
            <v-menu
                ref="dateMenu"
                v-model="dateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                    v-model="dateRangeText"
                    label="Période"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                    hide-details
                    class="rounded-lg"
                    clearable
                    @click:clear="clearDateFilter"
                ></v-text-field>
              </template>
              <v-date-picker
                  v-model="dates"
                  range
                  no-title
                  color="primary"
                  @input="dateMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="12" sm="4" class="text-right">
            <v-btn
                color="primary"
                class="elevation-1"
                :disabled="!clientslogs.length"
                @click="downloadCSV"
            >
              <v-icon left>mdi-download</v-icon>
              Exporter CSV
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Tableau des logs clients -->
      <v-data-table
          :headers="headers"
          :items="filteredLogs"
          :search="search"
          :loading="loading"
          :items-per-page.sync="itemsPerPage"
          :items-per-page-options="[10, 15, 25, 50]"
          :single-expand="singleExpand"
          :expanded.sync="expanded"
          :footer-props="{
          'items-per-page-text': 'Lignes par page',
          'items-per-page-options': [10, 15, 25, 50],
          'show-first-last-page': true,
          'show-current-page': true,
          'page-text': '{0}-{1} sur {2}'
        }"
          show-expand
          @click:row="expandRow"
          class="client-logs-table elevation-0"
      >
        <!-- ID avec badge -->
        <template v-slot:[`item.id`]="{ item }">
          <v-chip
              x-small
              outlined
              color="primary"
              class="font-weight-medium"
          >
            #{{ item.id }}
          </v-chip>
        </template>

        <!-- Formatage de la date -->
        <template v-slot:[`item.logDate`]="{ item }">
          <div class="d-flex flex-column">
            <span class="text-body-2 font-weight-medium">{{ formatDateOnly(item.logDate) }}</span>
            <span class="text-caption grey--text">{{ formatTimeOnly(item.logDate) }}</span>
          </div>
        </template>

        <!-- Empreinte digitale avec troncature et icône -->
        <template v-slot:[`item.fingerprint`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <div class="d-flex align-center" v-bind="attrs" v-on="on">
                <v-icon
                    small
                    color="primary"
                    class="mr-1"
                >
                  mdi-fingerprint
                </v-icon>
                <span class="text-truncate" style="max-width: 250px;">
                  {{ item.fingerprint || 'Non disponible' }}
                </span>
              </div>
            </template>
            <span>{{ item.fingerprint || 'Non disponible' }}</span>
          </v-tooltip>
        </template>

        <!-- Détails étendus -->
        <template v-slot:[`expanded-item`]="{ headers, item }">
          <td :colspan="headers.length" class="pa-4">
            <v-card flat class="expanded-details pa-3 rounded-lg grey lighten-5">
              <div class="d-flex mb-2">
                <v-chip color="primary" label small class="mr-2">Détails complets</v-chip>
                <v-spacer></v-spacer>
                <v-btn x-small text color="grey" @click="copyToClipboard(item)">
                  <v-icon small left>mdi-content-copy</v-icon>
                  Copier
                </v-btn>
              </div>
              <vue-json-pretty
                  :data="item"
                  :deep="2"
                  highlight-mouseover
                  :showDoubleQuotes="true"
                  :showLength="true"
              ></vue-json-pretty>
            </v-card>
          </td>
        </template>

        <!-- État vide -->
        <template v-slot:[`no-data`]>
          <v-card-text class="text-center py-6">
            <v-icon size="64" color="grey lighten-1">mdi-account-off-outline</v-icon>
            <div class="text-h6 grey--text text--darken-1 mt-2">Aucune donnée disponible</div>
            <div class="text-body-2 grey--text">Aucun log client n'a été trouvé</div>
          </v-card-text>
        </template>
      </v-data-table>
    </v-card>

    <!-- Snackbar pour les notifications -->
    <v-snackbar
        v-model="snackbar"
        :color="snackbarColor"
        timeout="3000"
        bottom
        right
    >
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn
            text
            v-bind="attrs"
            @click="snackbar = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import apiClient from "../../config/api.js";
import { format, parseISO, isToday } from 'date-fns';
import { fr } from 'date-fns/locale';

function isMobileDevice() {
  return /Mobi|Android/i.test(navigator.userAgent);
}

export default {
  data() {
    return {
      isMobile: isMobileDevice(),
      search: "",
      loading: true,
      refreshing: false,
      clientslogs: [],
      filteredLogs: [],
      headers: [
        { text: "ID", value: "id", width: "100px" },
        { text: "Date du journal", value: "logDate", width: "180px" },
        { text: "Empreinte digitale", value: "fingerprint" }
      ],
      expanded: [],
      singleExpand: true,
      itemsPerPage: 15,

      // Nouveaux champs pour les filtres
      dateMenu: false,
      dates: [],

      // Notifications
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success'
    };
  },
  computed: {
    dateRangeText() {
      if (!this.dates || this.dates.length === 0) return 'Toutes les dates';
      if (this.dates.length === 1) return format(parseISO(this.dates[0]), 'dd/MM/yyyy');
      if (this.dates.length === 2) {
        return `${format(parseISO(this.dates[0]), 'dd/MM/yyyy')} - ${format(parseISO(this.dates[1]), 'dd/MM/yyyy')}`;
      }
      return 'Période sélectionnée';
    },

    todayLogsCount() {
      return this.clientslogs.filter(log => {
        try {
          return isToday(new Date(log.logDate));
        } catch(e) {
          return false;
        }
      }).length;
    },

    uniqueFingerprintCount() {
      const uniqueFingerprints = new Set();
      this.clientslogs.forEach(log => {
        if (log.fingerprint) {
          uniqueFingerprints.add(log.fingerprint);
        }
      });
      return uniqueFingerprints.size;
    }
  },
  watch: {
    dates: {
      handler() {
        this.applyDateFilter();
      },
      deep: true
    }
  },
  created() {
    this.fetchData();
  },
  methods: {
    fetchData() {
      this.loading = true;
      apiClient
          .get(`${this.$store.getters.user.baseUrl}/admin/clientslogs`)
          .then(response => {
            this.clientslogs = response.data.datas;
            this.filteredLogs = [...this.clientslogs];
            this.loading = false;
          })
          .catch(error => {
            console.error("Error fetching client logs:", error);
            this.loading = false;
            this.showSnackbar('Erreur lors du chargement des logs clients', 'error');
          });
    },
    refreshData() {
      this.refreshing = true;
      apiClient
          .get(`${this.$store.getters.user.baseUrl}/admin/clientslogs`)
          .then(response => {
            this.clientslogs = response.data.datas;
            this.applyDateFilter();
            this.refreshing = false;
            this.showSnackbar('Logs clients actualisés avec succès', 'success');
          })
          .catch(error => {
            console.error("Error refreshing client logs:", error);
            this.refreshing = false;
            this.showSnackbar('Erreur lors de l\'actualisation des logs clients', 'error');
          });
    },
    formatDateOnly(dateString) {
      if (!dateString) return 'N/A';
      try {
        return format(new Date(dateString), 'dd MMM yyyy', {locale: fr});
      } catch (e) {
        return dateString;
      }
    },
    formatTimeOnly(dateString) {
      if (!dateString) return '';
      try {
        return format(new Date(dateString), 'HH:mm:ss');
      } catch (e) {
        return '';
      }
    },
    expandRow(item, {isExpanded}) {
      if (isExpanded) return;
      this.expanded = [item];
    },
    downloadCSV() {
      const csvContent = this.convertToCSV(this.filteredLogs);
      const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `client-logs-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.showSnackbar('Fichier CSV téléchargé', 'success');
    },
    convertToCSV(data) {
      const headers = ['ID', 'Date du journal', 'Empreinte digitale'];
      let csvRows = [];

      csvRows.push(headers.join(','));

      for (const log of data) {
        const row = [
          log.id,
          log.logDate,
          log.fingerprint || ''
        ];

        csvRows.push(row.map(value => `"${value}"`).join(','));
      }

      return csvRows.join('\n');
    },
    applyDateFilter() {
      if (!this.dates || this.dates.length === 0) {
        this.filteredLogs = [...this.clientslogs];
        return;
      }

      if (this.dates.length === 1) {
        // Un seul jour sélectionné
        const selectedDate = new Date(this.dates[0]);
        selectedDate.setHours(0, 0, 0, 0);
        const nextDay = new Date(selectedDate);
        nextDay.setDate(nextDay.getDate() + 1);

        this.filteredLogs = this.clientslogs.filter(log => {
          try {
            const logDate = new Date(log.logDate);
            return logDate >= selectedDate && logDate < nextDay;
          } catch (e) {
            return false;
          }
        });
      } else if (this.dates.length === 2) {
        // Période sélectionnée
        const startDate = new Date(this.dates[0]);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(this.dates[1]);
        endDate.setHours(23, 59, 59, 999);

        this.filteredLogs = this.clientslogs.filter(log => {
          try {
            const logDate = new Date(log.logDate);
            return logDate >= startDate && logDate <= endDate;
          } catch (e) {
            return false;
          }
        });
      }
    },
    clearDateFilter() {
      this.dates = [];
      this.filteredLogs = [...this.clientslogs];
    },
    copyToClipboard(item) {
      try {
        navigator.clipboard.writeText(JSON.stringify(item, null, 2));
        this.showSnackbar('Copié dans le presse-papier', 'success');
      } catch (err) {
        this.showSnackbar('Erreur lors de la copie', 'error');
      }
    },
    showSnackbar(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    dynamicID() {
      return 'dynamicID-' + Math.floor(Math.random() * Date.now().toString());
    }
  }
};
</script>

<style scoped>
.client-logs-header {
  background: linear-gradient(to right,  #01579B, #1e88e5);
}

.expanded-details {
  border-radius: 4px;
  overflow: auto;
  max-height: 400px;
}

.client-logs-table ::v-deep th {
  font-weight: bold !important;
  background-color: #f5f5f5 !important;
}

.client-logs-table ::v-deep tbody tr {
  cursor: pointer;
  transition: background-color 0.2s;
}

.client-logs-table ::v-deep tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.03) !important;
}

.client-logs-table ::v-deep tbody tr:nth-of-type(even) {
  background-color: rgba(240, 240, 240, 0.5);
}

.client-logs-table ::v-deep tbody tr:nth-of-type(odd) {
  background-color: rgb(250, 250, 250);
}

.client-logs-table ::v-deep tbody tr.v-data-table__expanded__content {
  box-shadow: inset 0 4px 4px -4px rgba(0, 0, 0, 0.1);
}
</style>